import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Calculator,
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import workOrderService from '../../services/productionService';
import warehouseService from '../../services/warehouseService';

const CompleteWorkOrderDialog = ({
  open,
  onClose,
  workOrder,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [calcResult, setCalcResult] = useState(null);
  const [linkedMovements, setLinkedMovements] = useState([]);

  const [actualMaterialsUsed, setActualMaterialsUsed] = useState(null);
  const [finishedGoodsLocation, setFinishedGoodsLocation] = useState('main');
  
  const [remainingBars, setRemainingBars] = useState([]);
  const [showAddRemaining, setShowAddRemaining] = useState(false);
  const [newRemainingBar, setNewRemainingBar] = useState({
    material_code: '',
    material_name: '',
    length: '',
    quantity: 1,
    location: 'main',
    product_id: ''
  });

  // Get available materials from linkedMovements (warehouse movements) or items' material_requirements
  const availableMaterials = useMemo(() => {
    // First try linkedMovements
    if (linkedMovements && linkedMovements.length > 0) {
      const materials = new Map();
      
      linkedMovements.forEach(move => {
        if (move.products && Array.isArray(move.products)) {
          move.products.forEach(prod => {
            const code = prod.product_id || prod.material_product_code || prod.product_code;
            const name = prod.product_name || prod.material_product_name || '';
            
            if (code && !materials.has(code)) {
              materials.set(code, {
                code,
                name,
                product_code: prod.finished_product_code || ''
              });
            }
          });
        }
      });
      
      if (materials.size > 0) {
        return Array.from(materials.values());
      }
    }
    
    // Fallback: get from items' material_requirements
    if (workOrder?.items) {
      const materials = new Map();
      workOrder.items.forEach(item => {
        if (item.material_requirements && Array.isArray(item.material_requirements)) {
          item.material_requirements.forEach(mat => {
            const code = mat.material_product_code || mat.material_code;
            if (code && !materials.has(code)) {
              materials.set(code, {
                code,
                name: mat.material_name || mat.material_product_name || '',
                product_code: item.finished_product_code || mat.product_code || ''
              });
            }
          });
        }
      });
      return Array.from(materials.values());
    }
    
    return [];
  }, [linkedMovements, workOrder]);

  const { totalCompleted, totalScrapped } = useMemo(() => {
    // Calculate completed quantity from process_steps for consistency
    if (workOrder?.items && workOrder.items.length > 0) {
      // Multi-item order: take the last step's completed quantity for each item
      // (items must pass through all steps, so last step = final completed count)
      let completed = 0;
      let scrapped = 0;
      workOrder.items.forEach(item => {
        if (item.process_steps && item.process_steps.length > 0) {
          // Get the last step (highest sequence) that has progress
          const steps = item.process_steps;
          const lastStep = steps.reduce((max, s) => s.sequence > max.sequence ? s : max, { sequence: -1, quantity_completed: 0, quantity_scrapped: 0 });
          completed += Number(lastStep.quantity_completed || 0);
          scrapped += Number(lastStep.quantity_scrapped || 0);
        }
      });
      return { totalCompleted: completed, totalScrapped: scrapped };
    }

    // Fallback: use progress_history or order fields
    const history = workOrder?.progress_history || [];
    const completed = history.reduce((sum, entry) => sum + (entry.completed_quantity || 0), 0);
    const scrapped = history.reduce((sum, entry) => sum + (entry.scrapped_quantity || 0), 0);
    return {
      totalCompleted: completed || Number(workOrder?.completed_quantity || 0),
      totalScrapped: scrapped || Number(workOrder?.scrap_quantity || 0)
    };
  }, [workOrder]);

  const loadData = useCallback(async () => {
    if (!workOrder) return;
    setLoading(true);
    setError(null);
    try {
      // Calculate ideal bars (optional, may fail for non-bar products)
      try {
        const calc = await workOrderService.calculateIdealBars(workOrder._id, 0);
        setCalcResult(calc);
      } catch (err) {
        console.error('Failed to calculate:', err);
      }

      // Load linked movements for material dropdown
      try {
        // Handle both object and array formats for material_issue_movements
        let movementIds = workOrder.material_issue_movements || [];
        if (typeof movementIds === 'object' && !Array.isArray(movementIds)) {
          // If it's an object with numeric/string keys, convert to array
          const values = Object.values(movementIds).filter(v => v);
          // Flatten in case values are arrays
          movementIds = values.flat().filter(v => v && typeof v === 'string');
        }
        
        if (movementIds.length > 0) {
          const movements = await warehouseService.getMovementsByIds(movementIds);
          setLinkedMovements(movements);
        } else {
          setLinkedMovements([]);
        }
      } catch (err) {
        console.error('Failed to load movements:', err);
        setLinkedMovements([]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [workOrder]);

  useEffect(() => {
    if (open && workOrder) {
      loadData();
      setRemainingBars([]);
      setFinishedGoodsLocation('main');
      setActualMaterialsUsed(null);
    }
  }, [open, workOrder, loadData]);

  const handleAddRemainingBar = () => {
    if (!newRemainingBar.material_code) {
      setError('请选择物料类型');
      return;
    }
    if (!newRemainingBar.length || parseFloat(newRemainingBar.length) <= 0) {
      setError('请输入有效的余料长度');
      return;
    }

    // Find selected material info
    const selectedMaterial = availableMaterials.find(
      m => m.code === newRemainingBar.material_code
    );

    setRemainingBars([...remainingBars, {
      ...newRemainingBar,
      id: Date.now(),
      material_code: newRemainingBar.material_code,
      material_name: selectedMaterial?.name || '',
      product_code: selectedMaterial?.product_code || '',
      length: parseFloat(newRemainingBar.length),
      quantity: parseInt(newRemainingBar.quantity) || 1
    }]);

    setNewRemainingBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: ''
    });
    setShowAddRemaining(false);
    setError(null);
  };

  const removeRemainingBar = (id) => {
    setRemainingBars(remainingBars.filter(bar => bar.id !== id));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const result = await workOrderService.completeProduction(
        workOrder._id,
        totalCompleted,
        totalScrapped,
        '',  // notes
        actualMaterialsUsed ? parseFloat(actualMaterialsUsed) : null,
        remainingBars
      );
      
      onSuccess?.(result);
      handleClose();
    } catch (err) {
      console.error('Failed to complete work order:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || '完成工单失败';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setRemainingBars([]);
    onClose();
  };

  const plannedQty = workOrder?.total_planned_quantity || workOrder?.planned_quantity || 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>完成工单</DialogTitle>
          <DialogDescription>
            确认工单 {workOrder?.production_order_id} 的完成信息
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {calcResult && (
              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Calculator className="h-4 w-4" />
                    物料消耗计算
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">棒料长度:</span>
                      <span className="ml-2">{calcResult.bar_length}mm</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">产品长度:</span>
                      <span className="ml-2">{calcResult.product_length}mm</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">每根产出:</span>
                      <span className="ml-2">{calcResult.pieces_per_bar}个</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">已领料:</span>
                      <span className="ml-2">{workOrder?.total_bars_issued || 0} 根</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>完成数量 (累计)</Label>
                  <div className="p-2 bg-muted rounded-md border text-sm font-medium">
                    {totalCompleted}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    计划数量: {plannedQty}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>报废数量 (累计)</Label>
                  <div className="p-2 bg-muted rounded-md border text-sm font-medium">
                    {totalScrapped}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>实际使用物料数量</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={actualMaterialsUsed || ''}
                    onChange={(e) => setActualMaterialsUsed(e.target.value)}
                    placeholder="手动输入实际消耗（可选）"
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    如不填写则不记录实际消耗
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>成品入库位置</Label>
                  <Input
                    value={finishedGoodsLocation}
                    onChange={(e) => setFinishedGoodsLocation(e.target.value)}
                    placeholder="输入入库位置"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">余料处理</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowAddRemaining(true)}
                    disabled={submitting || showAddRemaining}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    添加余料
                  </Button>
                </div>

                {showAddRemaining && (
                  <Card className="p-3 border-dashed">
                    <div className="space-y-3">
                      {/* Material Selection */}
                      <div className="space-y-1">
                        <Label className="text-xs">选择物料</Label>
                        <Select
                          value={newRemainingBar.material_code}
                          onValueChange={(value) => {
                            const material = availableMaterials.find(m => m.code === value);
                            setNewRemainingBar({
                              ...newRemainingBar,
                              material_code: value,
                              material_name: material?.name || '',
                              product_code: material?.product_code || ''
                            });
                          }}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="选择物料类型" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMaterials.map((material) => (
                              <SelectItem key={material.code} value={material.code}>
                                {material.code} - {material.name} ({material.product_code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">长度(mm)</Label>
                          <Input
                            type="number"
                            value={newRemainingBar.length}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, length: e.target.value})}
                            placeholder="长度"
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">数量(根)</Label>
                          <Input
                            type="number"
                            value={newRemainingBar.quantity}
                            onChange={(e) => setNewRemainingBar({...newRemainingBar, quantity: e.target.value})}
                            placeholder="数量"
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">位置</Label>
                        <Input
                          value={newRemainingBar.location}
                          onChange={(e) => setNewRemainingBar({...newRemainingBar, location: e.target.value})}
                          placeholder="位置"
                          className="h-8"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setShowAddRemaining(false)}>取消</Button>
                        <Button size="sm" onClick={handleAddRemainingBar}>确认添加</Button>
                      </div>
                    </div>
                  </Card>
                )}

                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>物料</TableHead>
                        <TableHead>长度</TableHead>
                        <TableHead>数量</TableHead>
                        <TableHead>位置</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {remainingBars.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-4">
                            无余料记录
                          </TableCell>
                        </TableRow>
                      ) : (
                        remainingBars.map((bar) => (
                          <TableRow key={bar.id}>
                            <TableCell className="font-medium">{bar.material_code}</TableCell>
                            <TableCell>{bar.length}mm</TableCell>
                            <TableCell>{bar.quantity}</TableCell>
                            <TableCell>{bar.location}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeRemainingBar(bar.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>
                取消
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                完成工单
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompleteWorkOrderDialog;